import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, CheckCircle2 } from "lucide-react";

export default function Alertes() {
  const [f, setF] = useState({
    email: "", city: "", postal_code: "", property_type: "", max_bouquet: "", max_monthly: "", min_rooms: "", honeypot: "",
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/alerts", {
        email: f.email,
        city: f.city || "",
        postal_code: f.postal_code || "",
        property_type: f.property_type || null,
        max_bouquet: f.max_bouquet ? Number(f.max_bouquet) : null,
        max_monthly: f.max_monthly ? Number(f.max_monthly) : null,
        min_rooms: f.min_rooms ? Number(f.min_rooms) : null,
        honeypot: f.honeypot,
      });
      setDone(true);
      toast.success("Alerte enregistrée. Un email de confirmation vous sera envoyé.");
    } catch { toast.error("Erreur, réessayez."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <SEO title="Créer une alerte annonce — venteaterme.fr" description="Recevez par email les nouvelles annonces en vente à terme correspondant à vos critères." />
      <section className="pt-24 pb-10 bg-navy text-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4">
            <Bell size={14} /> Alertes email
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight">Ne ratez aucune annonce.</h1>
          <p className="text-cream/80 mt-6 max-w-2xl">
            Précisez vos critères. Dès qu'une annonce publiée y correspond, vous recevez un email — un lien de désinscription est présent dans chaque envoi.
          </p>
          <p className="text-cream mt-4 max-w-2xl font-semibold">
            <span className="inline-block bg-accent-brand text-white text-xs uppercase tracking-widest font-bold px-3 py-1.5 mr-3">100&nbsp;% gratuit</span>
            Sans engagement, sans revente de données.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6">
          {done ? (
            <div className="bg-white border border-navy/10 p-10 text-center">
              <CheckCircle2 className="mx-auto text-accent-brand mb-3" size={48} strokeWidth={1.4} />
              <h2 className="font-serif text-3xl text-navy mb-3">Alerte activée</h2>
              <p className="text-navy/70">Vous recevrez un email dès qu'une nouvelle annonce validée correspondra à vos critères.</p>
              <Link to="/annonces" className="inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 font-bold tracking-wide mt-8 hover:bg-navy/90 transition-colors">
                Voir les annonces disponibles
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="alerte-form" className="bg-white border border-navy/10 p-8 md:p-10 space-y-5">
              <input type="text" name="honeypot" value={f.honeypot} onChange={(e) => set("honeypot", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />
              <div><Label>Email</Label><Input required type="email" data-testid="alerte-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-2 border-navy/20" /></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Ville</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="ex : Marseille" className="mt-2 border-navy/20" /></div>
                <div><Label>Code postal</Label><Input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="ex : 13003" className="mt-2 border-navy/20" /></div>
              </div>
              <div>
                <Label>Type de bien</Label>
                <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="alerte-type" className="mt-2"><SelectValue placeholder="Peu importe" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="libre">Vente à terme libre</SelectItem>
                    <SelectItem value="occupee">Vente à terme occupée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div><Label>Bouquet max (€)</Label><Input type="number" value={f.max_bouquet} onChange={(e) => set("max_bouquet", e.target.value)} className="mt-2 border-navy/20" /></div>
                <div><Label>Mensualité max (€)</Label><Input type="number" value={f.max_monthly} onChange={(e) => set("max_monthly", e.target.value)} className="mt-2 border-navy/20" /></div>
                <div><Label>Pièces min</Label><Input type="number" min={1} max={12} value={f.min_rooms} onChange={(e) => set("min_rooms", e.target.value)} className="mt-2 border-navy/20" /></div>
              </div>
              <button disabled={busy} data-testid="alerte-submit" className="w-full bg-accent-brand text-white py-4 font-bold tracking-wide hover:bg-[#B08D22] transition-colors disabled:opacity-60">
                {busy ? "Enregistrement..." : "Activer mon alerte"}
              </button>
              <p className="text-xs text-navy/55 border-t border-navy/10 pt-4">
                Nous ne transmettons jamais vos coordonnées à un tiers. Un lien de désinscription est présent dans chaque email. Voir la <Link to="/confidentialite" className="underline">politique de confidentialité</Link>.
              </p>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

export function UnsubscribeAlerte() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const token = params.get("token") || "";
  const [state, setState] = useState("idle"); // idle | done | error

  const doUnsub = async () => {
    try {
      await api.post(`/alerts/unsubscribe?token=${encodeURIComponent(token)}`);
      setState("done");
    } catch { setState("error"); }
  };

  return (
    <>
      <SEO title="Désinscription — venteaterme.fr" description="Désactivation de votre alerte email" />
      <section className="min-h-[70vh] flex items-center justify-center py-24 px-6">
        <div className="max-w-md w-full bg-white border border-navy/10 p-10 text-center">
          <Bell className="mx-auto text-accent-brand mb-4" size={40} strokeWidth={1.4} />
          <h1 className="font-serif text-3xl text-navy mb-4">Se désinscrire</h1>
          {state === "idle" && (
            <>
              <p className="text-sm text-navy/70 mb-6">Confirmez pour ne plus recevoir les alertes email associées à ce lien.</p>
              <button onClick={doUnsub} disabled={!token} data-testid="unsub-confirm" className="w-full bg-accent-brand text-white py-3 font-bold tracking-wide hover:bg-[#B08D22] disabled:opacity-50">
                Confirmer la désinscription
              </button>
              {!token && <p className="text-xs text-red-600 mt-3">Lien de désinscription invalide.</p>}
            </>
          )}
          {state === "done" && <p className="text-sm text-navy/70">Vous êtes bien désinscrit. Vous ne recevrez plus d'alertes.</p>}
          {state === "error" && <p className="text-sm text-red-600">Lien invalide ou déjà utilisé.</p>}
        </div>
      </section>
    </>
  );
}
